// src/Dashboard.js - Complete Mobile Responsive Version
import React, { useState, useEffect } from 'react';
import { 
  Bike, Calendar, FileText, DollarSign, TrendingUp, TrendingDown, Shield, AlertTriangle, Loader, 
  Database, RefreshCw, Building, Users, Clock, CheckCircle, XCircle, Phone, Mail, 
  BarChart3, PieChart, Activity, Target, Star, ArrowUp, ArrowDown, Eye
} from 'lucide-react';
import { motorbikes, getBikeModels, getModelCounts } from './bikeData';

// Import Firebase services
import { 
  subscribeToInvoices,
  subscribeToRentals,
  subscribeToReservations,
  getAllIncomeData,
  getRentals
} from './services/firebaseInvoiceService';

// Import DocumentFieldsService
import DocumentFieldsService from './Upload Doc Invoice/DocumentFieldsService';
import ReservationStatistics from './Reservations Management/ReservationStatistics';

// 🔧 SIMPLE FILTERING FUNCTION FOR CONSISTENT BOOKING COUNTS
const filterCompanyReservations = (allReservations, currentCompanyId) => {
  console.log('🔍 Dashboard Filtering:', {
    total: allReservations?.length || 0,
    companyId: currentCompanyId
  });

  if (!allReservations || !currentCompanyId) {
    return [];
  }

  // Simple filter: only include reservations with matching companyId
  const filtered = allReservations.filter(reservation => 
    reservation.companyId === currentCompanyId
  );

  console.log('📊 Dashboard Results:', {
    original: allReservations.length,
    filtered: filtered.length,
    excluded: allReservations.length - filtered.length
  });

  return filtered;
};

const Dashboard = ({ setActiveMenu, setSelectedBikeForEdit, currentUser, currentCompany, getCompanyContext, hasPermission }) => {

  // Existing state for Firebase data
  const [incomeData, setIncomeData] = useState({});
  const [expensesData, setExpensesData] = useState({});
  const [licenseIncome, setLicenseIncome] = useState(0);
  const [licenseExpenses, setLicenseExpenses] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [activeRentals, setActiveRentals] = useState([]);
  const [bikesWithDates, setBikesWithDates] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [expiringInsurance, setExpiringInsurance] = useState([]);

  // 📊 BOOKING STATISTICS STATE
  const [bookingStats, setBookingStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
    whatsappBookings: 0,
    emailBookings: 0,
    directBookings: 0,
    conversionRate: 0,
    averageBookingValue: 0,
    topBikes: [],
    recentBookings: [],
    bookingTrends: [],
    customerRetention: 0,
    dataIntegrity: null
  });

  const [inquiryStats, setInquiryStats] = useState({
    totalInquiries: 0,
    convertedInquiries: 0,
    pendingInquiries: 0,
    whatsappInquiries: 0,
    emailInquiries: 0,
    conversionRate: 0
  });

  const [isLoading, setIsLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('thisMonth');

  const companyId = getCompanyContext();

  // 📊 UPDATED BOOKING STATISTICS CALCULATION WITH CONSISTENT FILTERING
  const calculateBookingStatistics = (reservationsData, inquiriesData = []) => {
    console.log('📊 Dashboard: Starting booking statistics calculation...');
    
    if (!reservationsData || reservationsData.length === 0) {
      console.log('📊 Dashboard: No reservations data available');
      return {
        total: 0, today: 0, thisWeek: 0, thisMonth: 0,
        pending: 0, confirmed: 0, completed: 0, cancelled: 0,
        whatsappBookings: 0, emailBookings: 0, directBookings: 0,
        conversionRate: 0, averageBookingValue: 0,
        topBikes: [], recentBookings: [], bookingTrends: [],
        customerRetention: 0,
        dataIntegrity: {
          totalFiltered: 0,
          originalCount: 0,
          companyId: companyId
        }
      };
    }

    // ✅ APPLY CONSISTENT FILTERING (same as Reservations will use)
    const companyReservations = filterCompanyReservations(reservationsData, companyId);

    console.log(`📊 Dashboard: Processing ${companyReservations.length} filtered reservations (from ${reservationsData.length} total)`);

    if (companyReservations.length === 0) {
      return {
        total: 0, today: 0, thisWeek: 0, thisMonth: 0,
        pending: 0, confirmed: 0, completed: 0, cancelled: 0,
        whatsappBookings: 0, emailBookings: 0, directBookings: 0,
        conversionRate: 0, averageBookingValue: 0,
        topBikes: [], recentBookings: [], bookingTrends: [],
        customerRetention: 0,
        dataIntegrity: {
          totalFiltered: 0,
          originalCount: reservationsData.length,
          companyId: companyId
        }
      };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    // Status counts with validation
    const pending = companyReservations.filter(r => r.availabilityStatus === 'pending').length;
    const confirmed = companyReservations.filter(r => r.availabilityStatus === 'booked').length;
    const cancelled = companyReservations.filter(r => r.availabilityStatus === 'cancel').length;
    
    // Completed: past dropoff date and was booked
    const completed = companyReservations.filter(r => {
      if (!r.dropoffDateTime || r.availabilityStatus !== 'booked') return false;
      try {
        const dropoffDate = new Date(r.dropoffDateTime);
        return dropoffDate < now;
      } catch (error) {
        console.warn('⚠️ Invalid dropoff date:', r.dropoffDateTime);
        return false;
      }
    }).length;

    // Helper function for consistent date parsing
    const getCreatedDate = (reservation) => {
      if (reservation.createdAt?.seconds) {
        return new Date(reservation.createdAt.seconds * 1000);
      }
      if (reservation.createdAt) {
        return new Date(reservation.createdAt);
      }
      if (reservation.date) {
        return new Date(reservation.date);
      }
      return new Date(); // Fallback to now
    };

    // Time-based counts
    const todayBookings = companyReservations.filter(r => {
      const createdDate = getCreatedDate(r);
      return createdDate >= today;
    }).length;

    const weekBookings = companyReservations.filter(r => {
      const createdDate = getCreatedDate(r);
      return createdDate >= weekStart;
    }).length;

    const monthBookings = companyReservations.filter(r => {
      const createdDate = getCreatedDate(r);
      return createdDate >= monthStart;
    }).length;

    // Source-based counts with improved detection
    const whatsappBookings = companyReservations.filter(r => 
      r.inquirySource === 'whatsapp' || 
      (r.whatsappNumber && r.whatsappNumber.trim() !== '')
    ).length;
    
    const emailBookings = companyReservations.filter(r => 
      r.inquirySource === 'email' || 
      (r.email && r.email.trim() !== '' && (!r.whatsappNumber || r.whatsappNumber.trim() === ''))
    ).length;
    
    const directBookings = companyReservations.length - whatsappBookings - emailBookings;

    // Average booking value with better parsing
    const totalValue = companyReservations.reduce((sum, r) => {
      if (!r.rentalAmount) return sum;
      
      const cleanAmount = r.rentalAmount.toString().replace(/[^0-9.]/g, '');
      const amount = parseFloat(cleanAmount) || 0;
      return sum + amount;
    }, 0);
    
    const averageBookingValue = companyReservations.length > 0 ? totalValue / companyReservations.length : 0;

    // Top bikes
    const bikeFrequency = {};
    companyReservations.forEach(r => {
      if (r.motorbikeNumber && r.motorbikeNumber.trim() !== '') {
        const bike = r.motorbikeNumber.trim();
        bikeFrequency[bike] = (bikeFrequency[bike] || 0) + 1;
      }
    });
    
    const topBikes = Object.entries(bikeFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([bike, count]) => ({ bike, count }));

    // Recent bookings
    const recentBookings = companyReservations
      .sort((a, b) => {
        const dateA = getCreatedDate(a);
        const dateB = getCreatedDate(b);
        return dateB - dateA;
      })
      .slice(0, 10);

    // 7-day booking trends
    const bookingTrends = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayStart = new Date(date);
      const dayEnd = new Date(date);
      dayEnd.setDate(date.getDate() + 1);
      
      const dayBookings = companyReservations.filter(r => {
        const createdDate = getCreatedDate(r);
        return createdDate >= dayStart && createdDate < dayEnd;
      }).length;
      
      bookingTrends.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        bookings: dayBookings
      });
    }

    // Customer retention
    const customerFrequency = {};
    companyReservations.forEach(r => {
      const customerKey = (
        r.customerName?.toLowerCase().trim() || 
        r.email?.toLowerCase().trim() || 
        r.whatsappNumber?.trim() || 
        r.localNumber?.trim() ||
        'unknown'
      );
      
      if (customerKey !== 'unknown') {
        customerFrequency[customerKey] = (customerFrequency[customerKey] || 0) + 1;
      }
    });
    
    const repeatCustomers = Object.values(customerFrequency).filter(count => count > 1).length;
    const totalCustomers = Object.keys(customerFrequency).length;
    const customerRetention = totalCustomers > 0 ? (repeatCustomers / totalCustomers) * 100 : 0;

    // Conversion rate (simplified for now)
    const conversionRate = 0;

    const statistics = {
      total: companyReservations.length,
      today: todayBookings,
      thisWeek: weekBookings,
      thisMonth: monthBookings,
      pending,
      confirmed,
      completed,
      cancelled,
      whatsappBookings,
      emailBookings,
      directBookings,
      conversionRate,
      averageBookingValue,
      topBikes,
      recentBookings,
      bookingTrends,
      customerRetention,
      dataIntegrity: {
        totalFiltered: companyReservations.length,
        originalCount: reservationsData.length,
        companyId: companyId,
        filterStrict: true
      }
    };

    console.log('✅ Dashboard Booking Statistics Summary:', {
      company: companyId,
      total: statistics.total,
      thisMonth: statistics.thisMonth,
      originalCount: reservationsData.length,
      filteredCount: companyReservations.length
    });

    return statistics;
  };

  // Company-specific localStorage key generator
  const getCompanyStorageKey = (baseKey) => {
    return `${baseKey}_company_${companyId}`;
  };

  // Calculate date utilities
  const isExpiringWithinWeek = (dateString) => {
    if (!dateString) return false;
    
    const expiryDate = new Date(dateString);
    const today = new Date();
    
    expiryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const timeDiff = expiryDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff <= 7;
  };

  const daysUntilExpiry = (dateString) => {
    if (!dateString) return null;
    
    const expiryDate = new Date(dateString);
    const today = new Date();
    
    expiryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const timeDiff = expiryDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleSetSelectedBikeForEdit = (bike) => {
    if (typeof setSelectedBikeForEdit === 'function') {
      setSelectedBikeForEdit(bike);
    } else {
      console.error('setSelectedBikeForEdit is not a function or not provided');
    }
  };

  // Debug function for testing
  const debugBookingCounts = () => {
    console.log('🐛 DEBUG Dashboard - Current booking counts:');
    console.log('Company ID:', companyId);
    console.log('Raw reservations:', reservations?.length || 0);
    
    if (reservations && companyId) {
      const filtered = filterCompanyReservations(reservations, companyId);
      console.log('Filtered reservations:', filtered.length);
      
      const withCompanyId = reservations.filter(r => r.companyId);
      const withoutCompanyId = reservations.filter(r => !r.companyId);
      const currentCompanyData = reservations.filter(r => r.companyId === companyId);
      const otherCompanyData = reservations.filter(r => r.companyId && r.companyId !== companyId);
      
      console.log('Breakdown:', {
        total: reservations.length,
        withCompanyId: withCompanyId.length,
        withoutCompanyId: withoutCompanyId.length,
        currentCompany: currentCompanyData.length,
        otherCompanies: otherCompanyData.length,
        filtered: filtered.length
      });
    }
  };

  // Initialize Firebase data loading with company context
  useEffect(() => {
    if (!companyId) {
      setError('No company context available');
      setIsLoading(false);
      return;
    }

    const initializeDashboard = async () => {
      try {
        setConnectionStatus('connecting');
        setError(null);
        console.log(`Dashboard: Initializing Database connection for company ${companyId}...`);

        await Promise.all([
          loadCompanyIncomeData(),
          loadCompanyBikesData(),
          loadCompanyLicenseData()
        ]);

        setupCompanyRealtimeSubscriptions();
        
        setConnectionStatus('connected');
        setLastUpdated(new Date());
        
      } catch (error) {
        console.error('Dashboard: Error initializing Database:', error);
        setError('Failed to connect to Database: ' + error.message);
        setConnectionStatus('error');
      } finally {
        setIsLoading(false);
      }
    };

    initializeDashboard();
  }, [companyId]);

  // 📊 UPDATE BOOKING STATISTICS WHEN RESERVATIONS CHANGE
  useEffect(() => {
    if (reservations && reservations.length >= 0) {
      console.log('📊 Dashboard: Updating booking statistics with new reservation data...');
      console.log('📊 Dashboard: Raw reservations received:', reservations.length);
      
      const loadInquiriesData = async () => {
        try {
          const inquiriesKey = getCompanyStorageKey('inquiries');
          const savedInquiries = localStorage.getItem(inquiriesKey);
          const inquiriesData = savedInquiries ? JSON.parse(savedInquiries) : [];
          
          const newBookingStats = calculateBookingStatistics(reservations, inquiriesData);
          const newInquiryStats = calculateInquiryStatistics(inquiriesData);
          
          setBookingStats(newBookingStats);
          setInquiryStats(newInquiryStats);
          
          console.log('✅ Dashboard: Updated booking statistics');
          console.log(`   Total bookings: ${newBookingStats.total}`);
          console.log(`   Data integrity: Original ${newBookingStats.dataIntegrity?.originalCount} → Filtered ${newBookingStats.dataIntegrity?.totalFiltered}`);
        } catch (error) {
          console.error('❌ Dashboard: Error calculating booking statistics:', error);
        }
      };
      
      loadInquiriesData();
    }
  }, [reservations, companyId, currentCompany]);

  // Calculate inquiry statistics
  const calculateInquiryStatistics = (inquiriesData = []) => {
    const companyInquiries = inquiriesData.filter(inquiry => 
      !inquiry.companyId || inquiry.companyId === companyId
    );

    const totalInquiries = companyInquiries.length;
    const convertedInquiries = companyInquiries.filter(inq => inq.converted).length;
    const pendingInquiries = companyInquiries.filter(inq => !inq.converted && !inq.dismissed).length;
    const whatsappInquiries = companyInquiries.filter(inq => inq.source === 'whatsapp').length;
    const emailInquiries = companyInquiries.filter(inq => inq.source === 'email').length;
    const conversionRate = totalInquiries > 0 ? (convertedInquiries / totalInquiries) * 100 : 0;

    return {
      totalInquiries,
      convertedInquiries,
      pendingInquiries,
      whatsappInquiries,
      emailInquiries,
      conversionRate
    };
  };

  // Load company data functions
  const loadCompanyIncomeData = async () => {
    try {
      const incomeEntries = await getAllIncomeData(companyId);
      const groupedIncomeData = {};
      const groupedExpensesData = {};
      
      if (typeof incomeEntries === 'object' && !Array.isArray(incomeEntries)) {
        Object.entries(incomeEntries).forEach(([bikeId, bikeData]) => {
          if (bikeData.entries && Array.isArray(bikeData.entries)) {
            const incomeItems = [];
            const expenseItems = [];
            
            bikeData.entries.forEach(entry => {
              const item = {
                amount: entry.amount || 0,
                date: entry.date || entry.createdAt || new Date().toISOString(),
                invoice: entry.invoiceNumber || entry.description || 'N/A',
                description: entry.description || 'Entry',
                companyId: entry.companyId || companyId
              };
              
              if (entry.type === 'expense') {
                expenseItems.push(item);
              } else {
                incomeItems.push({...item, invoice: entry.invoiceNumber || 'N/A'});
              }
            });
            
            if (incomeItems.length > 0) groupedIncomeData[bikeId] = incomeItems;
            if (expenseItems.length > 0) groupedExpensesData[bikeId] = expenseItems;
          }
        });
      }
      
      setIncomeData(groupedIncomeData);
      setExpensesData(groupedExpensesData);
      
    } catch (error) {
      console.error('Error loading company income data:', error);
      loadFromCompanyLocalStorage();
    }
  };

  const loadCompanyBikesData = async () => {
    try {
      const documentFields = DocumentFieldsService.getDocumentFields();
      const companyDocumentFields = documentFields || [];
      
      if (companyDocumentFields && Array.isArray(companyDocumentFields)) {
        const bikesWithDatesData = companyDocumentFields.filter(field => 
          field.revenueLicenseExpiry || field.insuranceExpiry
        );
        
        setBikesWithDates(bikesWithDatesData);
        
        const licensesExpiring = bikesWithDatesData.filter(bike => 
          bike.revenueLicenseExpiry && isExpiringWithinWeek(bike.revenueLicenseExpiry)
        );
        
        const insuranceExpiring = bikesWithDatesData.filter(bike => 
          bike.insuranceExpiry && isExpiringWithinWeek(bike.insuranceExpiry)
        );
        
        setExpiringLicenses(licensesExpiring);
        setExpiringInsurance(insuranceExpiring);
      }
    } catch (error) {
      console.error('Error loading company bikes data:', error);
    }
  };

  const loadCompanyLicenseData = async () => {
    try {
      const incomeEntries = await getAllIncomeData(companyId);
      let licenseExpenseEntries = [];
      
      if (typeof incomeEntries === 'object' && !Array.isArray(incomeEntries)) {
        Object.values(incomeEntries).forEach(bikeData => {
          if (bikeData.entries && Array.isArray(bikeData.entries)) {
            const licenseExpenses = bikeData.entries.filter(entry => 
              entry.type === 'license-expense' || 
              (entry.description && entry.description.toLowerCase().includes('license'))
            );
            licenseExpenseEntries.push(...licenseExpenses);
          }
        });
      }
      
      setLicenseExpenses(licenseExpenseEntries.map(entry => ({
        amount: entry.amount || 0,
        date: entry.date || entry.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        description: entry.description || 'License expense',
        companyId: entry.companyId || companyId
      })));
      
    } catch (error) {
      console.error('Error loading company license data:', error);
    }
  };

  const loadFromCompanyLocalStorage = () => {
    try {
      const savedIncomeData = localStorage.getItem(getCompanyStorageKey("incomeData"));
      const savedExpensesData = localStorage.getItem(getCompanyStorageKey("expensesData"));
      const savedLicenseExpenses = localStorage.getItem(getCompanyStorageKey("licenseExpenses"));
      const storedBikes = localStorage.getItem(getCompanyStorageKey("managedBikes"));
      
      if (savedIncomeData) setIncomeData(JSON.parse(savedIncomeData));
      if (savedExpensesData) setExpensesData(JSON.parse(savedExpensesData));
      if (savedLicenseExpenses) setLicenseExpenses(JSON.parse(savedLicenseExpenses));
      
      if (storedBikes) {
        const bikes = JSON.parse(storedBikes);
        setBikesWithDates(bikes);
        
        const licensesExpiring = bikes.filter(bike => 
          bike.revenueLicenseExpiry && isExpiringWithinWeek(bike.revenueLicenseExpiry)
        );
        
        const insuranceExpiring = bikes.filter(bike => 
          bike.insuranceExpiry && isExpiringWithinWeek(bike.insuranceExpiry)
        );
        
        setExpiringLicenses(licensesExpiring);
        setExpiringInsurance(insuranceExpiring);
      }
      
      console.log(`Dashboard: Loaded data from company-specific localStorage for ${companyId}`);
    } catch (error) {
      console.error('Error loading from company localStorage:', error);
    }
  };

  const setupCompanyRealtimeSubscriptions = () => {
    try {
      const unsubscribeReservations = subscribeToReservations((reservationsData) => {
        console.log(`Dashboard: Received ${reservationsData.length} reservations from Database for company ${companyId}`);
        setReservations(reservationsData);
        setLastUpdated(new Date());
      }, companyId);

      const unsubscribeRentals = subscribeToRentals((rentalsData) => {
        console.log(`Dashboard: Received ${rentalsData.length} rentals from Database for company ${companyId}`);
        setActiveRentals(rentalsData);
        setLastUpdated(new Date());
      }, companyId);

      const unsubscribeInvoices = subscribeToInvoices((invoicesData) => {
        console.log(`Dashboard: Received ${invoicesData.length} invoices from Database for company ${companyId}`);
        updateFinancialDataFromInvoices(invoicesData);
        setLastUpdated(new Date());
      }, companyId);

      return () => {
        if (unsubscribeReservations) unsubscribeReservations();
        if (unsubscribeRentals) unsubscribeRentals();
        if (unsubscribeInvoices) unsubscribeInvoices();
      };
    } catch (error) {
      console.error('Error setting up company Database subscriptions:', error);
    }
  };

  const updateFinancialDataFromInvoices = (invoicesData) => {
    try {
      const groupedIncomeData = {};
      
      invoicesData.forEach(invoice => {
        const bikeId = invoice.bikeModel || 'unknown';
        
        if (!groupedIncomeData[bikeId]) {
          groupedIncomeData[bikeId] = [];
        }
        
        groupedIncomeData[bikeId].push({
          amount: invoice.totalCost || 0,
          date: invoice.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          invoice: invoice.invoiceNumber || 'N/A',
          description: `Rental income - ${invoice.customerName || 'Unknown'}`,
          companyId: invoice.companyId || companyId
        });
      });
      
      setIncomeData(groupedIncomeData);
    } catch (error) {
      console.error('Error updating financial data:', error);
    }
  };

  const refreshData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadCompanyIncomeData(),
        loadCompanyBikesData(),
        loadCompanyLicenseData()
      ]);
      setLastUpdated(new Date());
      setError(null);
    } catch (error) {
      setError('Failed to refresh data: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation functions
  const navigateToBikeManagement = (bike) => {
    if (hasPermission('bikeManagement')) {
      handleSetSelectedBikeForEdit(bike);
      setActiveMenu('bikeManagement');
    } else {
      alert('You do not have permission to access Bike Management');
    }
  };

  const navigateToLicenseManagement = () => {
    if (hasPermission('bikeManagement')) {
      if (expiringLicenses.length > 0) {
        handleSetSelectedBikeForEdit({
          ...expiringLicenses[0],
          showSection: 'license'
        });
      } else {
        handleSetSelectedBikeForEdit({ showSection: 'license' });
      }
      setActiveMenu('bikeManagement');
    } else {
      alert('You do not have permission to access License Management');
    }
  };

  const navigateToInsuranceManagement = () => {
    if (hasPermission('bikeManagement')) {
      if (expiringInsurance.length > 0) {
        handleSetSelectedBikeForEdit({
          ...expiringInsurance[0],
          showSection: 'insurance'
        });
      } else {
        handleSetSelectedBikeForEdit({ showSection: 'insurance' });
      }
      setActiveMenu('bikeManagement');
    } else {
      alert('You do not have permission to access Insurance Management');
    }
  };

  // Calculate statistics
  const totalBikes = motorbikes.length;
  const activeRentalsCount = activeRentals.filter(rental => 
    rental.status === 'Active' || rental.status === 'Reserved'
  ).length;
  const availableBikes = totalBikes - activeRentalsCount;
  
  const modelCounts = getModelCounts();
  
  const calculateOverallTotal = (data) => {
    return Object.values(data).flat().reduce((total, item) => total + item.amount, 0);
  };
  
  const overallIncome = calculateOverallTotal(incomeData);
  const overallExpenses = calculateOverallTotal(expensesData);
  const overallProfit = overallIncome - overallExpenses;
  
  const totalLicenseExpenses = licenseExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  const licenseProfitLoss = licenseIncome - totalLicenseExpenses;
  
  const getAllTransactions = () => {
    const incomeTransactions = Object.entries(incomeData).flatMap(([bikeId, transactions]) => 
      transactions.map(t => ({
        ...t,
        bikeId,
        type: 'income'
      }))
    );
    
    const expenseTransactions = Object.entries(expensesData).flatMap(([bikeId, transactions]) => 
      transactions.map(t => ({
        ...t,
        bikeId,
        type: 'expense'
      }))
    );
    
    return [...incomeTransactions, ...expenseTransactions]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  };
  
  const recentTransactions = getAllTransactions();

  // Connection status component
  const ConnectionStatus = () => {
    const statusConfig = {
      connecting: { 
        color: 'text-yellow-400', 
        icon: Loader, 
        text: 'Connecting to Database...', 
        bgColor: 'bg-yellow-900/20',
        borderColor: 'border-yellow-700',
        spin: true 
      },
      connected: { 
        color: 'text-green-400', 
        icon: Database, 
        text: 'Connected to Database', 
        bgColor: 'bg-green-900/20',
        borderColor: 'border-green-700',
        spin: false 
      },
      error: { 
        color: 'text-red-400', 
        icon: AlertTriangle, 
        text: 'Database Connection Error', 
        bgColor: 'bg-red-900/20',
        borderColor: 'border-red-700',
        spin: false 
      }
    };

    const { color, icon: Icon, text, bgColor, borderColor, spin } = statusConfig[connectionStatus];

    return (
      <div className={`flex items-center gap-2 ${color} ${bgColor} ${borderColor} border rounded-lg px-3 py-2 mb-3 sm:mb-4`}>
        <Icon size={14} className={`sm:w-4 sm:h-4 ${spin ? 'animate-spin' : ''}`} />
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-xs font-medium">{text}</span>
          {lastUpdated && connectionStatus === 'connected' && (
            <span className="text-xs opacity-75">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
        {connectionStatus === 'connected' && (
          <button
            onClick={refreshData}
            disabled={isLoading}
            className="ml-2 p-1 hover:bg-green-800/30 rounded transition-colors flex-shrink-0"
          >
            <RefreshCw size={12} className={`sm:w-3.5 sm:h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>
    );
  };

  // 📊 BOOKING STATISTICS CARDS COMPONENT
  const BookingStatisticsCards = () => (
    <div className="mb-6 sm:mb-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-white flex flex-wrap items-center gap-2">
            📊 Booking Statistics
            <div className="flex items-center text-green-400 text-xs sm:text-sm">
              <Database size={12} className="mr-1 sm:w-3.5 sm:h-3.5" />
              Live Data
            </div>
          </h3>
          <p className="text-gray-400 text-xs sm:text-sm mt-1">
            Real-time booking metrics for {currentCompany?.name}
            {bookingStats.dataIntegrity && (
              <span className="block sm:inline sm:ml-2 text-blue-400 mt-1 sm:mt-0">
                ({bookingStats.dataIntegrity.totalFiltered} of {bookingStats.dataIntegrity.originalCount} records)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-700 border border-gray-600 text-white rounded-lg px-2 sm:px-3 py-1 text-xs sm:text-sm w-full sm:w-auto"
          >
            <option value="today">Today</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
          </select>
        </div>
      </div>

      {/* Primary Booking Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-blue-400 text-xs sm:text-sm font-medium mb-1">Total Bookings</h3>
              <p className="text-xl sm:text-2xl font-bold text-white">{bookingStats.total}</p>
              <p className="text-xs text-blue-300 mt-1">Company bookings only</p>
            </div>
            <div className="p-1.5 sm:p-2 bg-blue-500/20 rounded-lg flex-shrink-0 ml-2">
              <Calendar size={16} className="text-blue-400 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-green-400 text-xs sm:text-sm font-medium mb-1">This Month</h3>
              <p className="text-xl sm:text-2xl font-bold text-white">{bookingStats.thisMonth}</p>
              <div className="flex items-center text-xs text-green-300 mt-1">
                <ArrowUp size={10} className="mr-1" />
                Active period
              </div>
            </div>
            <div className="p-1.5 sm:p-2 bg-green-500/20 rounded-lg flex-shrink-0 ml-2">
              <TrendingUp size={16} className="text-green-400 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-purple-400 text-xs sm:text-sm font-medium mb-1">Conversion Rate</h3>
              <p className="text-xl sm:text-2xl font-bold text-white">{bookingStats.conversionRate.toFixed(1)}%</p>
              <p className="text-xs text-purple-300 mt-1">Inquiries to bookings</p>
            </div>
            <div className="p-1.5 sm:p-2 bg-purple-500/20 rounded-lg flex-shrink-0 ml-2">
              <Target size={16} className="text-purple-400 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-lg p-3 sm:p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-yellow-400 text-xs sm:text-sm font-medium mb-1">Avg. Booking Value</h3>
              <p className="text-xl sm:text-2xl font-bold text-white">${bookingStats.averageBookingValue.toFixed(0)}</p>
              <p className="text-xs text-yellow-300 mt-1">Per reservation</p>
            </div>
            <div className="p-1.5 sm:p-2 bg-yellow-500/20 rounded-lg flex-shrink-0 ml-2">
              <DollarSign size={16} className="text-yellow-400 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 gap-2">
          <h4 className="text-white font-medium flex items-center gap-2 text-sm sm:text-base">
            <Eye size={16} className="text-blue-400 sm:w-4.5 sm:h-4.5" />
            Recent Bookings
          </h4>
          {hasPermission('reservations') && (
            <button 
              onClick={() => setActiveMenu('reservations')}
              className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm flex items-center gap-1 self-start sm:self-auto"
            >
              View All <ArrowUp size={12} className="rotate-45 sm:w-3.5 sm:h-3.5" />
            </button>
          )}
        </div>
        <div className="space-y-2">
          {bookingStats.recentBookings.slice(0, 5).map((booking, index) => (
            <div key={index} className="bg-gray-700 rounded-lg p-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium text-sm sm:text-base truncate">{booking.customerName}</div>
                <div className="text-gray-400 text-xs sm:text-sm">
                  {booking.motorbikeNumber} • {new Date(booking.createdAt?.seconds ? booking.createdAt.seconds * 1000 : booking.createdAt || booking.date).toLocaleDateString()}
                </div>
              </div>
              <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-1">
                <div className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${
                  booking.availabilityStatus === 'booked' ? 'bg-green-500/20 text-green-400' :
                  booking.availabilityStatus === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {booking.availabilityStatus}
                </div>
                <div className="text-gray-400 text-xs sm:text-sm">{booking.rentalAmount}</div>
              </div>
            </div>
          ))}
        </div>
        {bookingStats.recentBookings.length === 0 && (
          <div className="text-center text-gray-400 py-6 sm:py-8">
            <Calendar size={28} className="mx-auto mb-2 sm:mb-3 opacity-50 sm:w-8 sm:h-8" />
            <p className="text-sm sm:text-base">No recent bookings found</p>
            <p className="text-xs sm:text-sm mt-1">New bookings will appear here</p>
          </div>
        )}
      </div>
    </div>
  );

  // Loading screen
  if (isLoading && connectionStatus === 'connecting') {
    return (
      <div className="flex items-center justify-center py-8 sm:py-12 px-4">
        <div className="text-center">
          <Loader className="animate-spin text-orange-500 mx-auto mb-3 sm:mb-4" size={40} />
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Loading Dashboard</h2>
          <p className="text-gray-400 text-sm sm:text-base">Connecting to Database...</p>
          {currentCompany && (
            <p className="text-gray-500 text-xs sm:text-sm mt-2">Company: {currentCompany.name}</p>
          )}
        </div>
      </div>
    );
  }

  // Permission check
  if (!hasPermission('dashboard')) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <Shield className="mx-auto h-16 w-16 sm:h-20 sm:w-20 text-gray-400 mb-4 sm:mb-6" />
        <h3 className="text-xl sm:text-2xl font-semibold text-gray-300 mb-3 sm:mb-4">Access Denied</h3>
        <p className="text-gray-500 text-base sm:text-lg">You don't have permission to access the dashboard.</p>
        <p className="text-gray-600 text-xs sm:text-sm mt-2">Contact your company administrator for access.</p>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-4 md:px-6 py-4">
      {/* Debug button for development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="hidden md:block fixed top-2 right-2 z-50 bg-blue-600 text-white p-2 rounded text-xs">
          <div>Dashboard Bookings: {bookingStats.total}</div>
          <div>Raw Data: {reservations?.length || 0}</div>
          <button onClick={debugBookingCounts} className="mt-1 bg-white text-blue-600 px-2 py-1 rounded text-xs">
            Debug
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-5 gap-3">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-white">Dashboard Overview</h3>
          {currentCompany && (
            <div className="flex flex-wrap items-center gap-2 text-blue-400 text-xs sm:text-sm mt-1">
              <div className="flex items-center gap-1">
                <Building size={14} className="sm:w-4 sm:h-4" />
                <span className="truncate max-w-[150px] sm:max-w-none">{currentCompany.name}</span>
              </div>
              <span className="text-gray-500">•</span>
              <div className="flex items-center gap-1">
                <Users size={14} className="sm:w-4 sm:h-4" />
                <span>{currentUser?.role}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Connection Status */}
      <ConnectionStatus />

      {/* Error Display */}
      {error && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-900/50 border border-red-700 rounded-lg flex flex-col sm:flex-row sm:items-center gap-3">
          <AlertTriangle className="text-red-400 flex-shrink-0 self-start sm:self-auto" size={18} />
          <div className="flex-grow min-w-0">
            <p className="text-red-300 font-medium text-sm sm:text-base">Database Connection Error</p>
            <p className="text-red-400 text-xs sm:text-sm break-words">{error}</p>
          </div>
          <button
            onClick={refreshData}
            className="ml-0 sm:ml-3 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs sm:text-sm whitespace-nowrap self-start sm:self-auto"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* 📊 ENHANCED BOOKING STATISTICS SECTION */}
      <BookingStatisticsCards />
      
      {/* License & Insurance Expiry Alerts Section */}
      {(expiringLicenses.length > 0 || expiringInsurance.length > 0) && (
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <AlertTriangle size={18} className="text-yellow-400 sm:w-5 sm:h-5" />
            <h3 className="text-base sm:text-lg font-medium text-white">Expiring Documents</h3>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {/* Revenue License Expiry Alerts */}
            {expiringLicenses.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText size={16} className="text-yellow-400 sm:w-4.5 sm:h-4.5" />
                  <h4 className="text-white font-medium text-sm sm:text-base">Revenue Licenses Expiring Soon</h4>
                </div>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {expiringLicenses.map((bike) => {
                    const days = daysUntilExpiry(bike.revenueLicenseExpiry);
                    const isExpired = days < 0;
                    
                    return (
                      <div 
                        key={bike.id}
                        className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 rounded-lg ${
                          isExpired 
                            ? 'bg-red-500/10 border border-red-500/30' 
                            : 'bg-gray-700 border border-gray-600'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-white text-sm sm:text-base">{bike.model}</span>
                            <span className="text-xs bg-gray-600 px-2 py-0.5 rounded-full text-gray-300">
                              {bike.regNumber}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Expires: {formatDate(bike.revenueLicenseExpiry)}
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                          <div className={`text-xs sm:text-sm font-medium ${isExpired ? 'text-red-400' : 'text-yellow-400'}`}>
                            {isExpired 
                              ? `Expired ${Math.abs(days)} days ago` 
                              : `${days} day${days !== 1 ? 's' : ''}`}
                          </div>
                          <button 
                            onClick={() => navigateToBikeManagement({...bike, showSection: 'license'})}
                            className="bg-yellow-500/20 hover:bg-yellow-500/30 px-2 py-1 rounded text-yellow-400 text-xs whitespace-nowrap"
                          >
                            Update
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <button 
                  onClick={navigateToLicenseManagement}
                  className="mt-3 sm:mt-4 w-full bg-yellow-500/20 hover:bg-yellow-500/30 py-2 px-4 rounded-lg text-yellow-400 text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <FileText size={14} className="sm:w-4 sm:h-4" />
                  Manage All Licenses
                </button>
              </div>
            )}
            
            {/* Insurance Expiry Alerts */}
            {expiringInsurance.length > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield size={16} className="text-blue-400 sm:w-4.5 sm:h-4.5" />
                  <h4 className="text-white font-medium text-sm sm:text-base">Insurance Policies Expiring Soon</h4>
                </div>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {expiringInsurance.map((bike) => {
                    const days = daysUntilExpiry(bike.insuranceExpiry);
                    const isExpired = days < 0;
                    
                    return (
                      <div 
                        key={bike.id}
                        className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 rounded-lg ${
                          isExpired 
                            ? 'bg-red-500/10 border border-red-500/30' 
                            : 'bg-gray-700 border border-gray-600'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-white text-sm sm:text-base">{bike.model}</span>
                            <span className="text-xs bg-gray-600 px-2 py-0.5 rounded-full text-gray-300">
                              {bike.regNumber}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Expires: {formatDate(bike.insuranceExpiry)}
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                          <div className={`text-xs sm:text-sm font-medium ${isExpired ? 'text-red-400' : 'text-blue-400'}`}>
                            {isExpired 
                              ? `Expired ${Math.abs(days)} days ago` 
                              : `${days} day${days !== 1 ? 's' : ''}`}
                          </div>
                          <button 
                            onClick={() => navigateToBikeManagement({...bike, showSection: 'insurance'})}
                            className="bg-blue-500/20 hover:bg-blue-500/30 px-2 py-1 rounded text-blue-400 text-xs whitespace-nowrap"
                          >
                            Update
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <button 
                  onClick={navigateToInsuranceManagement}
                  className="mt-3 sm:mt-4 w-full bg-blue-500/20 hover:bg-blue-500/30 py-2 px-4 rounded-lg text-blue-400 text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
                >
                  <Shield size={14} className="sm:w-4 sm:h-4" />
                  Manage All Insurance
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-5">Bike Inventory Overview</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="bg-gray-700 border border-orange-500 rounded-lg p-3 sm:p-4 shadow-md">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-gray-400 text-xs sm:text-sm font-medium mb-1">Total Bikes</h3>
              <p className="text-xl sm:text-2xl font-bold text-white">{totalBikes}</p>
              <p className="text-xs text-gray-500 mt-1">Company fleet</p>
            </div>
            <div className="p-1.5 sm:p-2 bg-gray-600 rounded-lg flex-shrink-0 ml-2">
              <Bike size={18} className="text-orange-400 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-700 border border-blue-500 rounded-lg p-3 sm:p-4 shadow-md">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-gray-400 text-xs sm:text-sm font-medium mb-1">Active Rentals</h3>
              <p className="text-xl sm:text-2xl font-bold text-white">{activeRentalsCount}</p>
              <p className="text-xs text-gray-500 mt-1">Live from Database</p>
            </div>
            <div className="p-1.5 sm:p-2 bg-gray-600 rounded-lg flex-shrink-0 ml-2">
              <Calendar size={18} className="text-blue-400 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-700 border border-green-500 rounded-lg p-3 sm:p-4 shadow-md sm:col-span-2 lg:col-span-1">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h3 className="text-gray-400 text-xs sm:text-sm font-medium mb-1">Available Bikes</h3>
              <p className="text-xl sm:text-2xl font-bold text-white">{availableBikes}</p>
              <p className="text-xs text-gray-500 mt-1">Ready to rent</p>
            </div>
            <div className="p-1.5 sm:p-2 bg-gray-600 rounded-lg flex-shrink-0 ml-2">
              <FileText size={18} className="text-green-400 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Finance Overview Section */}
      <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">Finance Overview</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="bg-gray-700 rounded-lg border border-gray-600 p-4 sm:p-5 relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 bg-green-500/5 rounded-full transform translate-x-6 -translate-y-6"></div>
          <div className="absolute bottom-0 left-0 w-12 sm:w-16 h-12 sm:h-16 bg-green-500/10 rounded-full transform -translate-x-6 translate-y-6"></div>
          <div className="relative">
            <div className="flex items-center gap-2 text-green-400 mb-2">
              <TrendingUp size={16} className="sm:w-4.5 sm:h-4.5" />
              <div className="font-medium text-sm sm:text-base">Total Income</div>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white mb-2">${overallIncome.toFixed(2)}</div>
            <div className="text-xs text-gray-400 mb-2">
              From {Object.keys(incomeData).length} motorbikes
            </div>
            <div className="text-xs text-gray-500">
              🔥 Company data live
            </div>
            {hasPermission('financeManager') && (
              <button 
                onClick={() => setActiveMenu('financeManager')}
                className="mt-3 sm:mt-4 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded-lg text-xs text-green-400 flex items-center gap-1 transition-colors"
              >
                View Details
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-700 rounded-lg border border-gray-600 p-4 sm:p-5 relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 bg-red-500/5 rounded-full transform translate-x-6 -translate-y-6"></div>
          <div className="absolute bottom-0 left-0 w-12 sm:w-16 h-12 sm:h-16 bg-red-500/10 rounded-full transform -translate-x-6 translate-y-6"></div>
          <div className="relative">
            <div className="flex items-center gap-2 text-red-400 mb-2">
              <TrendingDown size={16} className="sm:w-4.5 sm:h-4.5" />
              <div className="font-medium text-sm sm:text-base">Total Expenses</div>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white mb-2">${overallExpenses.toFixed(2)}</div>
            <div className="text-xs text-gray-400 mb-2">
              From {Object.keys(expensesData).length} motorbikes
            </div>
            <div className="text-xs text-gray-500">
              🔥 Company data live
            </div>
            {hasPermission('financeManager') && (
              <button 
                onClick={() => setActiveMenu('financeManager')}
                className="mt-3 sm:mt-4 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded-lg text-xs text-red-400 flex items-center gap-1 transition-colors"
              >
                View Details
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-700 rounded-lg border border-gray-600 p-4 sm:p-5 relative overflow-hidden shadow-lg md:col-span-3 lg:col-span-1">
          <div className={`absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 ${overallProfit >= 0 ? 'bg-blue-500/5' : 'bg-orange-500/5'} rounded-full transform translate-x-6 -translate-y-6`}></div>
          <div className={`absolute bottom-0 left-0 w-12 sm:w-16 h-12 sm:h-16 ${overallProfit >= 0 ? 'bg-blue-500/10' : 'bg-orange-500/10'} rounded-full transform -translate-x-6 translate-y-6`}></div>
          <div className="relative">
            <div className={`flex items-center gap-2 ${overallProfit >= 0 ? 'text-blue-400' : 'text-orange-400'} mb-2`}>
              <DollarSign size={16} className="sm:w-4.5 sm:h-4.5" />
              <div className="font-medium text-sm sm:text-base">Net Profit</div>
            </div>
            <div className={`text-xl sm:text-2xl font-bold ${overallProfit >= 0 ? 'text-blue-400' : 'text-orange-400'} mb-2`}>
              ${overallProfit.toFixed(2)}
            </div>
            <div className="text-xs text-gray-400 mb-2">
              Margin: {overallIncome > 0 ? `${Math.round((overallProfit / overallIncome) * 100)}%` : "0%"}
            </div>
            <div className="text-xs text-gray-500">
              🔥 Company data live
            </div>
            {hasPermission('financeManager') && (
              <button 
                onClick={() => setActiveMenu('financeManager')}
                className={`mt-3 sm:mt-4 px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded-lg text-xs ${overallProfit >= 0 ? 'text-blue-400' : 'text-orange-400'} flex items-center gap-1 transition-colors`}
              >
                View Summary
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Recent Transactions and Bike Profitability */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
          <div className="border-b border-gray-700 px-3 sm:px-4 py-2.5 sm:py-3">
            <h3 className="font-medium text-white text-sm sm:text-base">Recent Transactions</h3>
          </div>
          <div className="p-3 sm:p-4">
            {recentTransactions.length > 0 ? (
              <div className="space-y-2">
                {recentTransactions.map((transaction, index) => (
                  <div 
                    key={index} 
                    className="bg-gray-700 rounded-lg p-2.5 sm:p-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1.5 sm:gap-0 border border-gray-600"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm text-gray-300 truncate">
                        {transaction.type === 'income' 
                          ? `Income: #${transaction.invoice}` 
                          : `Expense: ${transaction.description}`}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {new Date(transaction.date).toLocaleDateString()} • {transaction.bikeId}
                      </div>
                    </div>
                    <div className={`font-semibold text-sm sm:text-base ${transaction.type === 'income' ? 'text-green-400' : 'text-red-400'} self-end sm:self-auto`}>
                      ${transaction.amount.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-700 rounded-lg p-3 sm:p-4 text-center text-gray-400">
                <div className="text-xs sm:text-sm">No transactions recorded yet</div>
                <div className="text-xs text-gray-500 mt-1">Company data loading...</div>
              </div>
            )}
            
            {hasPermission('financeManager') && (
              <button 
                onClick={() => setActiveMenu('financeManager')}
                className="mt-3 sm:mt-4 w-full bg-gray-700 hover:bg-gray-600 py-2 px-4 rounded-lg text-gray-300 text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
              >
                View All Transactions
              </button>
            )}
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden">
          <div className="border-b border-gray-700 px-3 sm:px-4 py-2.5 sm:py-3">
            <h3 className="font-medium text-white text-sm sm:text-base">Bike Profitability</h3>
          </div>
          <div className="p-3 sm:p-4">
            {Object.keys(incomeData).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(incomeData)
                  .map(([bikeId, incomeItems]) => {
                    const totalIncome = incomeItems.reduce((sum, item) => sum + item.amount, 0);
                    const totalExpense = (expensesData[bikeId] || []).reduce((sum, item) => sum + item.amount, 0);
                    const profit = totalIncome - totalExpense;
                    
                    return { bikeId, profit, income: totalIncome, expenses: totalExpense };
                  })
                  .sort((a, b) => b.profit - a.profit)
                  .slice(0, 5)
                  .map((item, index) => (
                    <div 
                      key={index} 
                      className="bg-gray-700 rounded-lg p-2.5 sm:p-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1.5 sm:gap-0 border border-gray-600"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs sm:text-sm text-white truncate">{item.bikeId}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          Income: ${item.income.toFixed(2)} • Expenses: ${item.expenses.toFixed(2)}
                        </div>
                      </div>
                      <div className={`font-semibold text-sm sm:text-base ${item.profit >= 0 ? 'text-blue-400' : 'text-orange-400'} self-end sm:self-auto`}>
                        ${item.profit.toFixed(2)}
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="bg-gray-700 rounded-lg p-3 sm:p-4 text-center text-gray-400">
                <div className="text-xs sm:text-sm">No financial data recorded yet</div>
                <div className="text-xs text-gray-500 mt-1">Company data loading...</div>
              </div>
            )}
            
            {hasPermission('financeManager') && (
              <button 
                onClick={() => setActiveMenu('financeManager')}
                className="mt-3 sm:mt-4 w-full bg-gray-700 hover:bg-gray-600 py-2 px-4 rounded-lg text-gray-300 text-xs sm:text-sm flex items-center justify-center gap-2 transition-colors"
              >
                Manage Finances
              </button>
            )}
          </div>
        </div>
      </div>
      
      <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">Bike Models</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 mb-6 sm:mb-8">
        {getBikeModels().map(model => (
          <div 
            key={model} 
            className="bg-gray-700 rounded-lg p-2.5 sm:p-3 border border-gray-600 hover:border-orange-500 transition-colors cursor-pointer active:scale-95"
            onClick={() => {
              if (hasPermission('bikeAnalytics')) {
                setActiveMenu('bikeAnalytics');
              } else {
                alert('You do not have permission to access Bike Analytics');
              }
            }}
          >
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1.5 sm:gap-0 mb-1.5 sm:mb-2">
              <span className="font-medium text-white text-xs sm:text-sm truncate">{model}</span>
              <span className="bg-gray-600 text-orange-400 text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full self-start sm:self-auto">
                {modelCounts[model]}
              </span>
            </div>
            <div className="text-xs text-gray-400">Click to view details</div>
          </div>
        ))}
      </div>
      
      {/* Navigation to Reservations */}
      <div className="mt-6 sm:mt-8">
        {hasPermission('reservations') && (
          <button 
            onClick={() => setActiveMenu('reservations')}
            className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 active:scale-98 text-white py-2.5 sm:py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all text-sm sm:text-base"
          >
            <Calendar size={18} className="sm:w-5 sm:h-5" />
            View All Reservations
          </button>
        )}
      </div>
    </div>
  );
};

export default Dashboard;